import Loader from '@/components/ui/loader';
import { useEffect, useMemo, useState } from 'react';
import { useAccountStore } from '@/store/use-account-store';
import { CapitalStatus, NetworkType, TransferType } from '@/types/enums';
import { Button } from '../ui';
import RegisterDialog from './modals/register-dialog';

import { getPrivyPrimaryAddress, usePrivyAccount } from '@/hooks';
import { useIsMigrated } from '@/hooks/use-migration';
import {
  clearPrimaryPrivateApiHeaders,
  clearPrivateApiHeaders,
  updatePrimaryPrivateApiHeaders,
  updatePrivateApiHeaders,
} from '@/lib/api-clients/rest-client';
import GuideModal from './modals/guide-modal';

import WithdrawConfirmationModal from './modals/withdraw-confirmation-modal';
import { useLogin, usePrivy, useWallets } from '@privy-io/react-auth';
import { TransferDialog } from './modals/transfer-dialog';
import { cn, logConsole } from '@/lib/utils';
import { sendEvent } from '@/utils/analytics';
import { EventType } from '@/types';
import config from '@/config';
import SwitchModal from './modals/switch-modal';
import { useModalStore } from '@/store/use-modal-store';
import { useTranslation } from 'react-i18next';
import { useSetActiveWallet } from '@privy-io/wagmi';
import { defaultChain } from '@/lib/wagmi/config';
import useFundedAccount from '@/hooks/use-funded-account';
import useRegisterUser from '@/lib/api/register-user';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import AccountSelect from '@/features/account/components/account-select';
import AccountModal from './modals/account-modal';
import {
  handleCapitalStatusForStore,
  handleUserForStore,
} from '@/utils/handle-store-data';
import { useShallow } from 'zustand/react/shallow';
import { HandCoins } from 'lucide-react';

export default function ConnectButton() {
  const {
    address,
    hasAuth,
    hasPrimaryAuth,
    hasPrimaryAuthHydrated,
    hasAuthHydrated,
    capitalStatus,
  } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      hasAuth: state.hasAuth,
      hasPrimaryAuthHydrated: state.hasPrimaryAuthHydrated,
      hasAuthHydrated: state.hasAuthHydrated,
      hasPrimaryAuth: state.hasPrimaryAuth,
      capitalStatus: state.getCapitalStatus(state.address),
    })),
  );

  const { t } = useTranslation();

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrimaryRegisterModalOpen, setIsPrimaryRegisterModalOpen] =
    useState(false);

  const {
    authenticated,
    isConnected,
    isEmbedded,
    isPrimaryEmbedded,
    isPrimary,
    privyReady,
    networkType,
    address: primaryAddress,
    activeWallet: activePrivyWallet,
  } = usePrivyAccount();

  const authParams = useAccountStore(
    useShallow((state) => state.getAuthParams()),
  );
  const primaryAuthParams = useAccountStore(
    useShallow((state) => state.getAuthParams(primaryAddress)),
  );

  useEffect(() => {
    if (!address) return;
    logConsole(false, 'SWITCHED ACCOUNT: clearing orders and transfers');
    clearPrivateApiHeaders(['X-API-KEY']);
    const { setHasAuth } = useAccountStore.getState();
    if (!!authParams?.apiKey && address === authParams.address) {
      updatePrivateApiHeaders({
        'X-API-KEY': authParams.apiKey,
      });
      setHasAuth(true);
    } else {
      setHasAuth(false);
    }
  }, [address, authParams?.address, authParams?.apiKey]);

  useEffect(() => {
    const { setHasPrimaryAuth } = useAccountStore.getState();
    if (!!primaryAuthParams?.apiKey) {
      updatePrimaryPrivateApiHeaders({
        'X-API-KEY': primaryAuthParams.apiKey,
      });
      setHasPrimaryAuth(true);
    } else {
      clearPrimaryPrivateApiHeaders(['X-API-KEY']);
      setHasPrimaryAuth(false);
    }
  }, [primaryAuthParams?.address, primaryAuthParams?.apiKey]);

  const { isEval, isFundedOrEval, capitalStatusData } = useFundedAccount();
  const isMigrated = useIsMigrated();

  useEffect(() => {
    if (!capitalStatusData) return;
    handleCapitalStatusForStore(capitalStatusData, address);
  }, [address, capitalStatusData]);

  const { mutate: registerUser } = useRegisterUser();

  useEffect(() => {
    if (!hasAuthHydrated || !address) return;
    if (isConnected && !hasAuth) {
      if (isEmbedded) {
        registerUser({ address, rememberMe: true });
      } else {
        setIsRegisterModalOpen(true);
      }
    } else if (isConnected && hasAuth) {
      setIsRegisterModalOpen(false);
    }
  }, [hasAuthHydrated, address, hasAuth, isConnected, isEmbedded]);

  useEffect(() => {
    if (!hasPrimaryAuthHydrated || !primaryAddress || !address || isPrimary)
      return;
    if (isConnected && !hasPrimaryAuth) {
      if (isPrimaryEmbedded) {
        registerUser({ address: primaryAddress, rememberMe: true });
      } else {
        setIsPrimaryRegisterModalOpen(true);
      }
    } else if (isConnected && hasPrimaryAuth) {
      setIsPrimaryRegisterModalOpen(false);
    }
  }, [
    address,
    hasPrimaryAuthHydrated,
    primaryAddress,
    isPrimary,
    isConnected,
    hasPrimaryAuth,
    isPrimaryEmbedded,
  ]);

  const { wallets } = useWallets();
  const { setActiveWallet } = useSetActiveWallet();

  const activeWallet = useMemo(
    () => wallets.find((wallet) => wallet.address === address),
    [wallets, address],
  );

  useEffect(() => {
    if (!activeWallet) return;
    if (networkType === NetworkType.EVM) {
      const evmWallet = activeWallet;
      if (evmWallet.walletClientType === 'privy') {
        const chainId = (evmWallet.chainId as string).split(':')[1];
        if (
          defaultChain.id.toString() !== chainId &&
          window.location.pathname !== '/custom-network-withdraw'
        ) {
          evmWallet.switchChain(defaultChain.id);
        }
      }
      setActiveWallet(evmWallet);
    }
    // For Solana, no chain switching required
  }, [activeWallet, networkType]);

  const { address: evmAddress } = useAccount();

  const isWrongAddress =
    isConnected &&
    !!activePrivyWallet &&
    !isEmbedded &&
    !!address &&
    !!evmAddress &&
    networkType === NetworkType.EVM &&
    evmAddress !== address;

  useEffect(() => {
    useModalStore.setState({ isSwitchModalOpen: isWrongAddress });
  }, [isWrongAddress]);

  const { user } = usePrivy();

  useEffect(() => {
    // NOTE: fix for edge case where address is empty but user is authenticated
    if (!isConnected || !!address) return;
    const { setAddress } = useAccountStore.getState();
    const primaryAddress = getPrivyPrimaryAddress(user);
    if (!primaryAddress) return;
    setAddress(primaryAddress);
  }, [address, isConnected, user?.wallet?.address, user?.linkedAccounts]);

  const { login } = useLogin({
    onComplete: ({ user }) => handleUserForStore(user),
  });

  const onSignIn = () => {
    sendEvent(EventType.AUTH_FLOW_STARTED);
    login();
  };

  if (!privyReady) {
    return (
      <div className="flex items-center border-l border-border px-6 sm:px-8">
        <Loader className="w-11" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative z-40 flex h-full max-w-max gap-4 border-l border-border font-mono text-sm',
      )}
    >
      <div className="grid">
        {!isConnected || !address ? (
          <div className="flex w-full items-center justify-between gap-2 px-6 sm:px-7">
            <Button
              variant="contrast"
              size="xl"
              className="h-12 capitalize lg:px-12"
              disabled={authenticated}
              onClick={onSignIn}
            >
              {t('connectButton.signUp')}
            </Button>

            <Button
              variant="secondary"
              size="xl"
              className="h-12 capitalize lg:px-12"
              disabled={authenticated}
              onClick={onSignIn}
            >
              {t('connectButton.logIn')}
            </Button>
          </div>
        ) : (
          <div className="flex w-full justify-between gap-4 px-6 md:px-7">
            <AccountSelect />
            {isMigrated && (
              <div className="flex items-center md:hidden">
                <TransferDialog
                  orderType={TransferType.WITHDRAW}
                  triggerIcon={<HandCoins size={16} />}
                />
              </div>
            )}
            {(!isEval || isMigrated) && (
              <div className="hidden items-center gap-4 md:flex">
                <div className="flex h-10 items-center gap-2">
                  {isMigrated ? null : !config.isDevelopment && isEmbedded ? (
                    isFundedOrEval ? (
                      <Button
                        variant="contrast"
                        size="xl"
                        className="h-12 capitalize"
                        asChild
                      >
                        <Link to="/vest-capital#capital-plans">
                          {t('fundingModal.buyMore')}
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        variant="contrast"
                        size="xl"
                        className="h-12 capitalize"
                        asChild
                      >
                        <Link to="/deposit">{t('fundingModal.deposit')}</Link>
                      </Button>
                    )
                  ) : (
                    <TransferDialog orderType={TransferType.DEPOSIT} />
                  )}
                  {(isMigrated ||
                    !config.isDevelopment ||
                    capitalStatus === CapitalStatus.FUNDED) && (
                    <TransferDialog orderType={TransferType.WITHDRAW} />
                  )}
                </div>
              </div>
            )}
            <RegisterDialog
              open={isRegisterModalOpen}
              setOpen={setIsRegisterModalOpen}
            />
            <RegisterDialog
              isForPrimary
              open={isPrimaryRegisterModalOpen}
              setOpen={setIsPrimaryRegisterModalOpen}
            />
          </div>
        )}
        <GuideModal />
        <SwitchModal />
        <WithdrawConfirmationModal />
        <AccountModal />
      </div>
    </div>
  );
}
